'use es6';

import { Map as ImmutableMap, List, fromJS } from 'immutable';
import { ReportingPerf } from '../lib/performance';
import { stableStringify } from '../lib/stringify';
import { post } from '../request/http';
import { isConfigReport, isJourneyReport, isRelationalReport } from '../tsTypes/reportTypes';
import { getJourneyQuery, getReportDefinition } from '../report/reportGetters';
const MISSING_ERROR_MESSAGE = 'ERROR OCCURRED, NO MESSAGE PROVIDED';
const VISUALIZATION_STATUS = {
  REPORT_UI: 'REPORT_UI',
  REPORT_VISUALIZATION: 'REPORT_VISUALIZATION'
};
const applyReportFixes = report => {
  if (isConfigReport(report)) {
    const customFilters = report.getIn(['config', 'filters', 'custom'], List());

    /**
     * This ensures that the custom filters are passed to the
     * ingestion endpoint as an Array. For some custom reports
     * it is instead stored as an object, so we have to perform
     * this transformation to ensure the request doesn't error.
     */
    if (ImmutableMap.isMap(customFilters)) {
      return report.updateIn(['config', 'filters', 'custom'], custom => fromJS([custom]));
    }
  }
  return report;
};
export const getIngestionBody = ({
  report,
  visualizationStatus,
  timeToResolveInMilliseconds,
  error,
  reportIngestionOptions = {}
}) => {
  const updatedReport = applyReportFixes(report);
  const insightParams = updatedReport.get('insightParams');
  const baseIngestionBody = {
    reportId: updatedReport.get('id'),
    errorMessage: error && (error.message || MISSING_ERROR_MESSAGE),
    timeToResolveInMilliseconds,
    insightParams: insightParams ? insightParams.toJS() : {}
  };
  if (isJourneyReport(updatedReport)) {
    return Object.assign({}, baseIngestionBody, {
      journeyQuery: getJourneyQuery(updatedReport).toJS()
    });
  }
  if (isRelationalReport(updatedReport)) {
    const reportDefinition = getReportDefinition(updatedReport);
    return Object.assign({}, baseIngestionBody, {
      reportId: reportIngestionOptions.reportId,
      reportDefinition: reportDefinition ? reportDefinition.toJS() : undefined
    });
  }
  return Object.assign({}, baseIngestionBody, {
    configQuery: {
      config: updatedReport.get('config').toJS(),
      configVisualization: {
        chartType: updatedReport.get('chartType'),
        displayParams: updatedReport.get('displayParams', ImmutableMap()).map(stableStringify).toJS()
      },
      reportConfigs: updatedReport.get('reportConfigs', ImmutableMap()).toJS()
    },
    visualizationStatus
  });
};
export class ReportingIngestionInteraction {
  constructor(report, reportIngestionOptions = {}) {
    this.report = report;
    if (!isJourneyReport(report) || !isRelationalReport(report)) {
      this.visualizationStatus = reportIngestionOptions.enabledForViz ? VISUALIZATION_STATUS.REPORT_VISUALIZATION : VISUALIZATION_STATUS.REPORT_UI;
    }
    this.reportIngestionOptions = reportIngestionOptions;
    this.perf = new ReportingPerf();
    this.perf.markStart();
  }
  executeInBackground(loggingFunctionAsync) {
    return loggingFunctionAsync().catch(() => {
      /*
          This is a background process, so errors are being swallowed so that an
          issue with ingestion does not activley break a report Resolve.
        */
    });
  }
  logSuccess() {
    return this.executeInBackground(() => post('reporting-usage-service/v1/ingestion/report', {
      data: getIngestionBody({
        report: this.report,
        visualizationStatus: this.visualizationStatus,
        timeToResolveInMilliseconds: this.perf.markEnd(),
        reportIngestionOptions: this.reportIngestionOptions
      })
    }));
  }
  logError(error) {
    return this.executeInBackground(() => post('reporting-usage-service/v1/ingestion/report', {
      data: getIngestionBody({
        report: this.report,
        visualizationStatus: this.visualizationStatus,
        error,
        timeToResolveInMilliseconds: this.perf.markEnd(),
        reportIngestionOptions: this.reportIngestionOptions
      })
    }));
  }
}